import React, { createContext, useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        try {
          const snap = await getDoc(doc(db, 'users', fbUser.uid));
          const profile = snap.data() || {};
          setUser({
            id: fbUser.uid,
            email: fbUser.email,
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
          });
          const t = await fbUser.getIdToken();
          setToken(t);
        } catch (e) {
          setUser({
            id: fbUser.uid,
            email: fbUser.email,
            firstName: '',
            lastName: '',
          });
          setToken(await fbUser.getIdToken());
        }
      } else {
        setUser(null);
        setToken(null);
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    setIsLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return true;
    } catch (err) {
      setError(err.message || 'Login failed');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email, password, firstName, lastName) => {
    setIsLoading(true);
    setError(null);
    try {
      const { user: fbUser } = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, 'users', fbUser.uid), { firstName, lastName, email });
      return true;
    } catch (err) {
      setError(err.message || 'Registration failed');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    signOut(auth);
    setUser(null);
    setToken(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, isLoading, error, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
